// RatesSimulator.js
import { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  TextField,
  Card,
  CardContent,
} from "@mui/material";

// Utility to pick random item(s) from a pool
function pickFromPool(pool, count) {
  const picks = [];
  for (let i = 0; i < count; i++) {
    const randomIndex = Math.floor(Math.random() * pool.length);
    picks.push(pool[randomIndex]);
  }
  return picks;
}

// Resolve an outcome into actual items
function resolveOutcome(outcome, pools) {
  const items = [];

  for (const itemDef of outcome.items) {
    if (itemDef.fromPool) {
      // Pick random item(s) from pool
      const pool = pools[itemDef.fromPool];
      const picked = pickFromPool(pool, itemDef.count);
      items.push(...picked.map(p => ({ ...p, quantity: 1 })));
    } else if (itemDef.specific) {
      // Use specific item
      items.push({
        name: itemDef.specific,
        image: itemDef.image || "/images/placeholder.png",
        quantity: itemDef.quantity || 1
      });
    } else if (itemDef.name) {
      // Direct item definition
      items.push({
        name: itemDef.name,
        image: itemDef.image,
        quantity: itemDef.quantity || 1
      });
    }
  }

  return items;
}

function getRandomResult(outcomes, pools) {
  const r = Math.random();
  let sum = 0;
  for (let outcome of outcomes) {
    sum += outcome.weight;
    if (r <= sum) {
      return resolveOutcome(outcome, pools);
    }
  }
  return resolveOutcome(outcomes[outcomes.length - 1], pools);
}

function simulateOpens(outcomes, pools, times) {
  const counts = {};
  for (let i = 0; i < times; i++) {
    const items = getRandomResult(outcomes, pools);
    items.forEach((item) => {
      const key = item.name;
      counts[key] = (counts[key] || 0) + item.quantity;
    });
  }
  return counts;
}

// Get all possible items for display
function getAllPossibleItems(outcomes, pools) {
  const itemsMap = new Map();

  for (const outcome of outcomes) {
    for (const itemDef of outcome.items) {
      if (itemDef.fromPool) {
        // Add all items from pool
        const pool = pools[itemDef.fromPool];
        pool.forEach(poolItem => {
          if (!itemsMap.has(poolItem.name)) {
            itemsMap.set(poolItem.name, { ...poolItem, quantity: 1 });
          }
        });
      } else if (itemDef.specific) {
        if (!itemsMap.has(itemDef.specific)) {
          itemsMap.set(itemDef.specific, {
            name: itemDef.specific,
            image: itemDef.image || "/images/placeholder.png",
            quantity: itemDef.quantity || 1
          });
        }
      } else if (itemDef.name) {
        if (!itemsMap.has(itemDef.name)) {
          itemsMap.set(itemDef.name, {
            name: itemDef.name,
            image: itemDef.image,
            quantity: itemDef.quantity || 1
          });
        }
      }
    }
  }

  return Array.from(itemsMap.values());
}

export default function RatesSimulator({ boxName, boxImage, outcomes, pools = {}, description }) {
  const [custom, setCustom] = useState(1);
  const [simulation, setSimulation] = useState(null);

  const handleOpen = (times) => {
    const counts = simulateOpens(outcomes, pools, times);
    setSimulation({ times, counts });
  };

  const allItems = getAllPossibleItems(outcomes, pools);

  return (
    <Box textAlign="center" p={2}>
      <Typography variant="h4" gutterBottom
        sx={theme => ({
          color: theme.palette.accent.main
        })}>
        {boxName}

      </Typography>
      {description &&
        <Typography mb={2}>
          {description}
        </Typography>
      }

      <img
        src={boxImage}
        alt={boxName}
        style={{ maxWidth: 200, marginBottom: 16 }}
      />

      {/* Buttons */}
      <Box mb={2}>
        <Button onClick={() => handleOpen(1)} variant="contained" sx={{ m: 1 }}>
          Open 1
        </Button>
        <Button onClick={() => handleOpen(6)} variant="contained" sx={{ m: 1 }}>
          Open 5-Bundle (6)
        </Button>
        <Button onClick={() => handleOpen(13)} variant="contained" sx={{ m: 1 }}>
          Open 10-Bundle (13)
        </Button>
        <Box mt={2}>
          <TextField
            type="number"
            label="Open (1-100)"
            size="small"
            inputProps={{ min: 1, max: 100 }}
            value={custom}
            onChange={(e) =>
              setCustom(Math.min(100, Math.max(1, parseInt(e.target.value) || 1)))
            }
            sx={{ width: 120, mr: 1 }}
          />
          <Button onClick={() => handleOpen(custom)} variant="outlined">
            Open
          </Button>
        </Box>
      </Box>

      {/* Simulation Results */}
      {simulation && (
        <>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ mt: 4, mb: 3 }}>
            <Typography variant="h5" sx={{ pr: 4, pl: 4 }} >
              Simulated results after {simulation.times} opening{simulation.times !== 1 ? 's' : ''}
            </Typography>

            <Button onClick={() => setSimulation(null)} variant="contained">
              Clear
            </Button>
          </Box>
          <Grid container spacing={2} justifyContent="center" mb={4}>
            {Object.entries(simulation.counts).map(([name, qty]) => {
              const found = allItems.find((i) => i.name === name);
              return (
                <Grid item key={name}>
                  <Card
                    sx={{ p: 1, borderRadius: 3, boxShadow: 3, minWidth: 120, maxWidth: 140 }}
                  >
                    <CardContent>
                      <Box textAlign="center">
                        <img
                          src={found?.image}
                          alt={name}
                          style={{ maxWidth: 80 }}
                        />
                        <Typography>
                          {qty}x {name}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {/* Possible Outcomes */}
      <Typography variant="h5" gutterBottom sx={theme => ({
              color: theme.palette.secondary.main
            })}>
        Possible Outcomes
      </Typography>
      <Grid container spacing={1.5} justifyContent="center" mb={4}>
        {outcomes.map((outcome, idx) => (
          <Grid item key={idx}>
            <Card sx={{ p: 1, borderRadius: 3, boxShadow: 2, minWidth: 100 }}>
              <CardContent>
                {outcome.items.map((itemDef, j) => {
                  if (itemDef.fromPool) {
                    // const pool = pools[itemDef.fromPool];
                    return (
                      <Box key={j} textAlign="center" mb={1}>
                        {itemDef.box ? (<img
                          src={"https://images.neopets.com/items/mall_giftbox_basic.gif"}
                          alt={"basic gift box"}
                          style={{ maxWidth: 80 }}
                        />): (<img
                          src={"https://images.neopets.com/winter/shopofmystery_item.gif"}
                          alt={"regular item"}
                          style={{ maxWidth: 80 }}
                        />)}
                        <Typography color="text.secondary">
                          {itemDef.buyable ? `${itemDef.count}x Buyable Item` : (itemDef.box ? `${itemDef.count}x Basic Gift Box` : `${itemDef.count}x Reg Item From Pool`)}
                        </Typography>
                      </Box>
                    );
                  } else if (itemDef.specific || itemDef.name) {
                    const name = itemDef.specific || itemDef.name;
                    const qty = itemDef.quantity || 1;
                    return (
                      <Box key={j} textAlign="center" mb={1}>
                        <img
                          src={itemDef.image || "https://images.neopets.com/winter/shopofmystery_item.gif"}
                          alt={name}
                          style={{ maxWidth: 80 }}
                        />
                        <Typography>
                          {qty}x {name}
                        </Typography>
                      </Box>
                    );
                  }
                  return null;
                })}
                {outcome.weight === 1 ?
                  (<Typography color="text.secondary" fontWeight="bold">
                    {((1 / pools.items.length) * 100).toFixed(1)}% chance of each item from pool (equal chance)
                  </Typography>) :
                  (<Typography color="text.secondary" fontWeight="bold">
                    Probability: {(outcome.weight * 100).toFixed(1)}%
                  </Typography>)
                }
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Possible Items */}
      <Typography variant="h5" gutterBottom sx={theme => ({
              color: theme.palette.secondary.main
            })}>
        Possible Items
      </Typography>
      <Grid container spacing={1.5} justifyContent="center">
        {allItems.map((item, idx) => (
          <Grid item key={idx}>
            <Card sx={{ p: 1, borderRadius: 3, boxShadow: 3, width: 140 }}>
              <CardContent>
                <Box textAlign="center">
                  <img
                    src={item.image}
                    alt={item.name}
                    style={{ maxWidth: 80 }}
                  />
                  <Typography variant="body2">{item.name}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}